import { useEffect, useState } from "react";
import { FiPhone, FiMail, FiMapPin, FiCheckCircle } from "react-icons/fi";

// import api from "../../api/api";

const BASE_API = (process.env.REACT_APP_BASE_API || "").replace(/\/+$/, "");

/* Shown until the saved content loads, and used if the API fails */
const DEFAULTS = {
  heading: "Get In Touch",
  subheading: "We're here to help you",
  phone: "+91 98765 43210",
  phoneHours: "Mon - Sat, 9am - 6pm",
  email: "support@jeetayu.com",
  emailNote: "We reply within 24 hours",
  addressLines: ["408-40C Sumer Nagar Extension", "Jaipur, Rajasthan 302020"],
  mapQuery: "Sumer Nagar Extension, Jaipur, Rajasthan 302020",
  formTitle: "Send us a message",
};

const inputCls =
  "w-full h-[46px] border border-[#d6e0d0] bg-white px-4 text-[14px] text-[#1f2d22] rounded-md focus:outline-none focus:border-[#2f6b3a] transition-colors";

function InfoRow({ Icon, title, lines }) {
  return (
    <div className="flex items-start gap-4 bg-white border border-[#e3e9dd] rounded-lg p-4">
      <span className="w-[42px] h-[42px] rounded-full bg-[#e6f1e1] text-[#2f6b3a] flex items-center justify-center shrink-0">
        <Icon className="text-[18px]" />
      </span>
      <div className="min-w-0">
        <p className="text-[12px] text-[#6b7a6d]">{title}</p>
        {lines.map((l, i) => (
          <p
            key={i}
            className={
              i === 0
                ? "text-[14px] sm:text-[15px] font-semibold text-[#1f2d22] break-words"
                : "text-[13px] text-[#6b7a6d] break-words"
            }
          >
            {l}
          </p>
        ))}
      </div>
    </div>
  );
}

function Contact() {
  const [c, setC] = useState(DEFAULTS);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [serverError, setServerError] = useState("");

  /* Load the content saved from the admin panel */
  useEffect(() => {
    const controller = new AbortController();
    fetch(`${BASE_API}/contact`, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setC({ ...DEFAULTS, ...d }))
      .catch(() => {}); // keep the defaults
    return () => controller.abort();
  }, []);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setSent(false);
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Enter a valid email address.";
    if (form.message.trim().length < 10)
      e.message = "Message should be at least 10 characters.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    setSent(false);
    if (!validate()) return;

    try {
      setLoading(true);

      // Option A (current): opens the visitor's email app with the message filled in.
      const subject = encodeURIComponent(`Message from ${form.name}`);
      const body = encodeURIComponent(
        `${form.message}\n\n— ${form.name} (${form.email})`
      );
      window.location.href = `mailto:${c.email}?subject=${subject}&body=${body}`;

      // Option B: send to your own backend instead. Uncomment the api import at the top,
      // then replace Option A with:
      // await api.post("/contact", form);

      setSent(true);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setServerError(
        err.response?.data?.message || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-[60px] min-h-screen bg-[#f6f8f2]">
      <div className="w-full max-w-[1100px] mx-auto px-4 py-8 sm:py-12">
        {/* Heading */}
        <div className="mb-8">
          <h1 className="font-serif text-[28px] sm:text-[36px] text-[#1f4a2b] leading-tight">
            {c.heading}
          </h1>
          <p className="text-[14px] text-[#6b7a6d] mt-1">{c.subheading}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start">
          {/* Left: details + map */}
          <div className="flex flex-col gap-4">
            <InfoRow Icon={FiPhone} title="Call us" lines={[c.phone, c.phoneHours]} />
            <InfoRow Icon={FiMail} title="Email us" lines={[c.email, c.emailNote]} />
            <InfoRow Icon={FiMapPin} title="Visit us" lines={c.addressLines} />

            <div className="bg-white border border-[#e3e9dd] rounded-lg overflow-hidden">
              <iframe
                title="Our location"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(
                  c.mapQuery
                )}&z=15&output=embed`}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-[240px] sm:h-[280px] border-0"
              />
            </div>
          </div>

          {/* Right: form */}
          <form
            onSubmit={handleSubmit}
            noValidate
            className="bg-white border border-[#e3e9dd] rounded-lg p-5 sm:p-8"
          >
            <h2 className="text-[18px] font-semibold text-[#1f4a2b] mb-5">
              {c.formTitle}
            </h2>

            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-[13px] text-[#3d4a3f] mb-1.5">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  value={form.name}
                  onChange={set("name")}
                  autoComplete="name"
                  className={`${inputCls} ${errors.name ? "border-red-400" : ""}`}
                />
                {errors.name && <p className="mt-1 text-[12px] text-red-500">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-[13px] text-[#3d4a3f] mb-1.5">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  autoComplete="email"
                  className={`${inputCls} ${errors.email ? "border-red-400" : ""}`}
                />
                {errors.email && <p className="mt-1 text-[12px] text-red-500">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-[13px] text-[#3d4a3f] mb-1.5">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={6}
                  value={form.message}
                  onChange={set("message")}
                  className={`w-full border border-[#d6e0d0] bg-white px-4 py-3 text-[14px] text-[#1f2d22] rounded-md focus:outline-none focus:border-[#2f6b3a] transition-colors resize-y ${
                    errors.message ? "border-red-400" : ""
                  }`}
                />
                {errors.message && (
                  <p className="mt-1 text-[12px] text-red-500">{errors.message}</p>
                )}
              </div>
            </div>

            {serverError && <p className="mt-4 text-[14px] text-red-500">{serverError}</p>}

            {sent && (
              <p className="mt-4 flex items-center gap-2 text-[14px] text-[#1f4a2b]">
                <FiCheckCircle className="text-[#2f6b3a] text-[18px] shrink-0" />
                Thanks! Your email app should open with your message ready to send.
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full mt-6 h-[48px] bg-[#2f6b3a] hover:bg-[#1f4a2b] disabled:opacity-60 transition-colors text-white text-[14px] font-semibold rounded-md"
            >
              {loading ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Contact;